import styled, { css } from 'styled-components';
import { Navbar, NavbarBrand, NavbarToggler, Collapse, NavItem, Nav } from 'reactstrap';

/** Largura da sidebar com menu expandido (hover desktop / drawer mobile). */
export const MENU_EXPANDED_WIDTH = '220px';

export const StyledNav = styled(Nav)`
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const StyledSelectArrow = styled.div`
  color: ${props => props.theme.input_color};
  ${props => {
    if (props.isOpen) {
      return 'transform: rotate(180deg); padding-bottom: 0.7rem;';
    }

    return '';
  }};
  ${props =>
    css`
      ${props.customstyle}
    `};
`;

export const DisabledLink = styled.a`
  cursor: default;

  ${props => {
    if (props.disabled) {
      return css`
        cursor: no-drop;
        opacity: .6;
      `};
    }
  }}

  @media (max-width: 800px) {
    font-weight: normal !important;
  }
`;

export const IconContainer = styled.div`
  display: flex;
  height: 50px;
  min-width: 48px;
  justify-content: center;
  align-items: center;
  margin-left: 0;
  border-radius: 12px;
  transition: background 0.2s ease;

  & > svg {
    margin-left: 9px !important;
    transition: transform 0.22s ease, filter 0.22s ease;
  }
`;

/** Chevron do menu — não estende StyledSelectArrow (evita padding/transform herdados que deslocam o glifo). */
export const Arrow = styled.div`
  position: relative;
  z-index: 1;
  display: none;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  margin: 0;
  padding: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
  align-items: center;
  justify-content: center;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'none')};
  transform-origin: 50% 50%;
  transition: color 0.2s ease, transform 0.2s ease;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 0;
    transform: translateY(1px);
  }
`;

export const StyledNavItem = styled(NavItem)`
  margin-bottom: 4px;
  border-radius: 0 12px 12px 0;
  overflow: hidden;
  background: ${props =>
    props.isOpen
      ? 'linear-gradient(90deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.04) 72%, transparent 100%)'
      : 'transparent'};
  border-left: 3px solid ${props => (props.isOpen ? '#ff7200' : 'transparent')};
  box-shadow: ${props =>
    props.isOpen
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 2px 10px rgba(0, 0, 0, 0.1)'
      : 'none'};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover,
  &.active {
    background: ${props =>
      props.isOpen
        ? 'linear-gradient(90deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.06) 72%, transparent 100%)'
        : 'rgba(255, 255, 255, 0.06)'};
  }

  div.collapse.show {
    display: none;
  }
`;

export const StyledNavLink = styled.li`
  list-style: none;
  padding: 0 6px 4px 0;
  margin-left: 6px;
  min-height: 30px;
  z-index: 8;
  display: none;

  .offset-1 {
    margin-left: 0;
  }

  a {
    display: inline-block;
    margin-left: 18px;
    padding: 7px 12px;
    border-radius: 8px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.78rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    transition: color 0.2s ease, background 0.2s ease, font-weight 0.15s ease;

    &:hover {
      font-weight: ${props => (props.disabled ? '' : '600')};
      color: #fff;
      background: rgba(255, 255, 255, 0.08);
    }

    &.active {
      font-weight: 700;
      color: #ffb366;
      background: linear-gradient(
        90deg,
        rgba(255, 114, 0, 0.22) 0%,
        rgba(255, 114, 0, 0.06) 100%
      );
      box-shadow: inset 0 0 0 1px rgba(255, 114, 0, 0.25);
    }
  }

  ${DisabledLink} {
    margin-left: 18px;
    padding: 7px 12px;
    border-radius: 8px;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.88);
    transition: background 0.2s ease;
  }

  ${DisabledLink}.active {
    color: #ffb366;
    font-weight: 700;
    background: linear-gradient(
      90deg,
      rgba(255, 114, 0, 0.2) 0%,
      rgba(255, 114, 0, 0.05) 100%
    );
  }

  @media (max-width: 800px) {
    a {
      display: block;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      min-height: 44px;
      margin-left: 10px;
      padding: 10px 14px;
      line-height: 1.35;
      white-space: normal;
      word-break: break-word;
    }

    ${DisabledLink} {
      display: block;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      min-height: 44px;
      margin-left: 10px;
      padding: 10px 14px;
      line-height: 1.35;
      white-space: normal;
      word-break: break-word;
    }
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  min-height: 48px;
  padding: 2px 4px 2px 0;
  cursor: pointer;
  border-radius: 0 12px 12px 0;
  transition: background 0.22s ease, box-shadow 0.22s ease, width 0.5s ease-in;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
  }

  &:hover ${IconContainer} > svg {
    transform: scale(1.06);
    filter: drop-shadow(0 0 10px rgba(255, 114, 0, 0.45));
  }

  &:hover ${Arrow} {
    color: rgba(255, 255, 255, 0.95);
  }
`;

export const ChevronWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: auto;
  color: rgba(255, 255, 255, 0.85);
  transition: color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 32px;
    height: 32px;
    margin-left: -16px;
    margin-top: -16px;
    border-radius: 50%;
    background: transparent;
    transition: background 0.2s ease;
    z-index: 0;
    pointer-events: none;
  }

  ${TitleContainer}:hover &::before {
    background: rgba(255, 255, 255, 0.12);
  }

  ${TitleContainer}:hover & {
    color: #fff;
  }
`;

export const Title = styled.span`
  display: none;

  @media (max-width: 800px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 0;
    margin: 0;
  }
`;

export const MainTitle = styled.span`
  display: none;

  @media (max-width: 800px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: auto !important;
    margin: 0;
    text-transform: uppercase;
    font-weight: 300;
  }
`;

export const StyledBrand = styled(NavbarBrand)`
  height: 42px;
  margin-right: 0;
  transition: filter 0.25s ease, opacity 0.2s ease;

  &:hover {
    filter: brightness(1.12) saturate(1.05);
  }

  g.symbol {
    display: block;
  }

  g.full {
    display: none;
  }

  svg.logo {
    width: 65px;
    height: 65px;
  }

  @media (max-width: 800px) {
    width: 50px;
    height: 30px;
    display: flex;
    align-items: center;

    g.symbol {
      display: block;
    }

    g.full {
      display: none;
    }

    svg.logo {
      width: 50px;
      height: 30px;
    }

    svg.path {
      width: 50px;
      height: 30px;
    }

    :hover svg {
      width: 100% !important;
    }
  }
`;

export const CollapsableContainer = styled(Collapse)`
  align-items: flex-start;
  padding-top: 44px;
  padding-bottom: 32px;
  width: 100%;
  ${props =>
    css`
      ${props.customstyle}
    `};
  
  transition: width 0.5s ease-in;
  
  @media (max-width: 800px) {
    position: relative;
    top: 75px;
    padding-top: 10px;
    
    &.no-flex {
      display: none !important;
    }
  };
`;

export const MainNav = styled(Navbar)`
  flex-direction: column;
  width: 65px;
  height: 100%;
  background: linear-gradient(
    165deg,
    #1a3562 0%,
    #152b4e 38%,
    #0f2239 72%,
    #0a1829 100%
  );
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.18);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  position: fixed;
  top: 0;
  font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  z-index: 100;
  font-size: 1rem;
  color: ${props => props.theme.color_white};
  padding: 0;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 801px) {
    overflow-x: hidden;
  }

  svg.nano {
    width: 1.8rem;
  }

  button:nth-of-type(2) {
    width: 58%;
    & > span {
      display: none;
    }
  }

  svg.sub-icon {
    width: 1rem;
    display: inline-block;
    margin: 0;
  }

  &:hover,
  &.active {
    width: ${MENU_EXPANDED_WIDTH};

    svg.nano {
      margin-left: 20px;
    }

    ${Title} {
      display: block;
      flex: 1;
      min-width: 0;
      width: auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 4px;
      transition: opacity 0.2s ease;
      font-weight: 500;
      letter-spacing: 0.04em;
      color: rgba(255, 255, 255, 0.94);
    }

    ${MainTitle} {
      @media (min-width: 800px) {
        display: none;
        width: 100px;
      }

      @media (max-width: 800px) {
        display: block;
        width: 100px;
      }
    }

    ${StyledBrand} {
      .no-expanded-logo,
      g.symbol {
        @media(min-width: 801px){
          display: none;
        }
      }

      g.full {
        display: block;
      }

      svg {
        width: 150px;
        height: auto;
      }
      .expanded-logo {
        @media(min-width: 801px){
          display: block !important;
          width: 141px;
          margin-top: 20px;
        }
      }

      @-moz-document url-prefix() {
        svg {
          width: 150px;
        }
      }
    }

    ${StyledNavItem} {
      div.collapse.show {
        display: block;
        min-width: 65px;
      }

      ${StyledNavLink} {
        display: block;
        transition: height 0.5s ease-in-out;
      }
    }

    ${IconContainer} {
      margin-left: 0;

      & > svg {
        margin-left: 7px !important;
      }
    }

    ${Arrow} {
      display: flex;
    }

    button:nth-of-type(2) {
      width: 90%;
      & > span {
        display: block;
      }
    }
  }

  @media (max-width: 800px) {
    &:hover {
      transition: none;
      width: 100%;

      ${StyledBrand} {
        g.symbol {
          display: block;
        }
  
        g.full {
          display: none;
        }

        svg {
          width: 50px !important;
          height: 30px !important;
        }
      }
    }
  }

  @media (max-width: 800px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 42px;
    width: 100%;
    transition: opacity 0.1s, width 0.1s 0.1s;

    ${StyledBrand} {
      img {
        top: 8px !important;
      }
    }

    &:hover {
      ${StyledBrand} {
        img {
          top: 20px;
          left: 10px !important;
          height: 28px !important;
          transform: none !important;
        }
      }
    }
  }
`;

export const NewRequestContainer = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0.4rem 0.75rem;
  margin: 50px 8px 8px 8px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: linear-gradient(
    165deg,
    #ffc56b 0%,
    #ff9f2e 18%,
    #ff7200 45%,
    #e85d00 72%,
    #c44a00 100%
  );
  color: #fff;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.02em;
  box-shadow:
    0 2px 8px rgba(255, 114, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;

  &:hover {
    filter: brightness(1.05) saturate(1.05);
    background: linear-gradient(
      165deg,
      #ffd085 0%,
      #ffab45 18%,
      #ff8519 45%,
      #f06500 72%,
      #d45500 100%
    );
    box-shadow:
      0 4px 16px rgba(255, 114, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.32),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.98);
  }

  &:focus {
    outline: 0;
    box-shadow:
      0 2px 8px rgba(255, 114, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.2),
      0 0 0 2px rgba(255, 255, 255, 0.35);
  }

  & > svg {
    margin-left: 7px !important;
  }

  @media (max-width: 800px) {
    display: none;
  }
`;

export const MobileNewRequestContainer = styled.button`
  display: none;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0.4rem 0.75rem;
  margin: 0 10px 5px 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: linear-gradient(
    165deg,
    #ffc56b 0%,
    #ff9f2e 18%,
    #ff7200 45%,
    #e85d00 72%,
    #c44a00 100%
  );
  color: #fff;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.02em;
  box-shadow:
    0 2px 8px rgba(255, 114, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;

  &:hover {
    filter: brightness(1.05) saturate(1.05);
    background: linear-gradient(
      165deg,
      #ffd085 0%,
      #ffab45 18%,
      #ff8519 45%,
      #f06500 72%,
      #d45500 100%
    );
    box-shadow:
      0 4px 16px rgba(255, 114, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.32),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:focus {
    outline: 0;
  }

  @media (max-width: 800px) {
    display: flex;
    margin: 10px 0 20px 14px;
  }
`;

export const TextRequest = styled.div`
  text-align: center;
  padding: 0.2rem;
  line-height: 0.6;
  font-size: 39.45px;
  color: #fff;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35), 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 800px) {
    margin-left: 0.3rem;
    padding: 0.3rem;
  }
`;

export const TitleRequestButton = styled.span`
  font-size: 12px;
  min-width: 122px;
  color: #fff;
`;

export const Lightbox = styled.div`
  display: none;
  ${props => {
    if (props.toggle === true) {
      return `
      display: block;
      position: fixed;
      width: 100%;
      height: 100%;
      background-color: rgba(15, 34, 57, 0.55);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      overflow: hidden;
      z-index: 2;
      bottom: 0;
      `;
    }
  }}

  @media (min-width: 801px) {
    display: none
  }
`;

export const Sidebar = styled.div`
  position: fixed;
  top: 0;
  right: ${props => (props.toggle ? 0 : `-${MENU_EXPANDED_WIDTH}`)};
  height: 100%;
  width: ${MENU_EXPANDED_WIDTH};
  padding: 0;
  overflow: auto;
  flex-direction: column;
  float: left;
  z-index: 3;
  font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  background: linear-gradient(
    165deg,
    #1a3562 0%,
    #152b4e 38%,
    #0f2239 72%,
    #0a1829 100%
  );
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.25);
  color: ${props => props.theme.color_white};
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (min-width: 801px) {
    display: none;
  }
`;

/** Fechar drawer mobile — canto superior direito, alinhado ao painel (220px). */
export const SidebarCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 159, 46, 0.55);
  }

  img {
    width: 17px;
    height: 17px;
    display: block;
  }
`;

export const UserIconContainer = styled.div`
  position: relative;
  top: -25px;
  right: -20px;
  height: auto;
  width: 28px;
  font-size: 11px;
  line-height: 10px;
  color: ${props => (props.color && props.color) || props.theme.color_white};
  cursor: pointer;

  & > svg {
    fill: ${props => (props.color && props.color) || props.theme.color_white};
  }
`;

export const SpanHello = styled.div`
  position: absolute;
  top: -11px;
  left: 48px;
  color: ${props => (props.color && props.color) || props.theme.color_gray};
  font-size: 12px;
  line-height: 0.7rem;
`;

export const NameUser = styled.a`
  color: ${props =>
    (props.color && props.color) || props.theme.color_gray} !important;
  font-weight: 600;
  padding-right: 10px !important;
  font-size: 12px;
  line-height: 10px;
  position: relative;

  &:before {
    content: '';
    width: calc(100% - 10px);
    height: 1px;
    background: #fff;
    position: absolute;
    bottom: 1px;
  }

  &:after {
    width: 10px;
    height: 4px;
  }
`;

export const BoxExit = styled.div`
  align-self: center;
  display: none;

  @media (max-width: 800px) {
    display: block;
    width: 20px !important;
    height: auto;
    position: relative;
    top: 40px;
    right: -140px;
  }
`;

export const ExitIconContainer = styled.div`
  position: relative;
  right: 125px;
  height: auto;
  width: 28px;
  font-size: 11px;
  line-height: 10px;
  color: ${props => (props.color && props.color) || props.theme.color_white};
  cursor: pointer;

  & > svg {
    & > path {
      fill: ${props => (props.color && props.color) || props.theme.color_white};
    }
  }
`;

export const ExitTitleContainer = styled.div`
  position: absolute;
  top: 5px;
  right: -40px;
  height: auto;
  width: 28px;
  font-size: 1rem;
  line-height: 10px;
  color: ${props => (props.color && props.color) || props.theme.color_white};
  cursor: pointer;
`;

export const StyledNavbarToggler = styled(NavbarToggler)`
  display: none;

  @media (max-width: 800px) {
    width: 46px;
    height: 30px;
    display: block !important;
  }
`;
